import "../../lib/RichGenerator";
import MouseTrap from "mousetrap";
import { Constants, SYSTEM_STATE } from "../../model/Constants";
import { addMessageBubbleToTranscriptInChatWindow } from "../../controller/TranscriptController";
import { renderRichMediaWidget } from "../bubbleviews/controller/BubbleController";
import ModalMgr from "../../util/ModalMgr";
import { fileuploadmodalviewRender } from "../templates/renders/file-upload-modal-view";

/**
 * Contains code to display file upload modal
 * @type {{template,
 * bindKeyboardShortcuts(): void,
 * displayRichFileUpload(): void,
 * getFileUploadJSON(): any,
 * removed(): void,
 * created(): void,
 * setError(string): void,
 * tagName: string,
 * onCancelClick: FileUploadModalView.onCancelClick,
 * resetError(): void,
 * onOkClick: FileUploadModalView.onOkClick,
 * unBindKeyboardShortcuts(): void,
 * onFileUpload(): void,
 * uploadHandlerCallback(): void}}
 */
const FileUploadModalView: any = {
    tagName: "file-upload-modal-view",
    renderer: fileuploadmodalviewRender,
    initialize(options) {
        const ciAppInstance = options.$app;
        const viewConstants = options.constants;
        const config = ciAppInstance.getUIConfig();

        const appStringProps = config.appStringProps;
        viewConstants.uploadBtnText = appStringProps.uploadModalOkButtonText || Constants.OK;
        viewConstants.cancelBtnText = appStringProps.uploadModalCancelButtonText || Constants.CANCEL;
        viewConstants.disclaimerText = appStringProps.uploadModalDisclaimerText || "";
        viewConstants.btnStyles = config.modalButtonStyles;
    },
    created() {
        this.mouseTrap = new MouseTrap(this.el);
        this.sdkInstance = this.$app.getSDKInstance();
        this.displayRichFileUpload();
        this.bindKeyboardShortcuts();
        this.okButton = this.el.querySelector(".file-ok-button");
    },

    /**
     * Bind keyboard shortcuts
     */
    bindKeyboardShortcuts() {
        this.mouseTrap.bind("escape", () => this.onCancelClick());
        this.mouseTrap.bind("enter", () => this.onSendClick());
        this.mouseTrap.bind("tab", (e) => ModalMgr.onTab(e, this.el));
        this.mouseTrap.bind("shift+tab", (e) => ModalMgr.onShiftTab(e, this.el));
    },

    /**
     * Populating file upload modal with a rich media file upload widget
     */
    displayRichFileUpload() {
        const uploadJson = this.getFileUploadJSON();

        if (uploadJson) {
            this.richInstance = window.RichMain.instance;
            uploadJson.lockView = false;
            const container = this.el.querySelector("#file-upload-container");
            renderRichMediaWidget(this.richInstance, this.$app, uploadJson, container);
            this.richInstance.registerCBListener(this.onFileUpload.bind(this));
        } else {
            this.onCancelClick();
        }
    },

    /**
     * Callback when file input changes for the Rich Media FileUpload
     * @param {{upload:{files:[File]}}} data File upload data from Rich Media control
     */
    onFileUpload(data) {
        this.resetError();

        if (data.upload) {
            this.files = data.upload.files;
        }
    },

    /**
     * Get the file upload Json from UIConfig
     * @return {Object} RichMedia JSON Object
     */
    getFileUploadJSON() {
        return this.$app.getUIConfig().fileUploadJSON;
    },

    /**
     * Handles removing the File view from Modal
     */
    onCancelClick() {
        this.el.parentElement.removeChild(this.el);
        ModalMgr.removeNegativeTabIndexOnClosingModal();
    },

    /**
     * Sending uploaded file to backend and get the necessary status through a callback
     */
    onOkClick() {
        const appStringProps = this.$app.getUIConfig().appStringProps;

        if (this.files && this.files.length > 0) {
            // disable ok button until fileupload api returns success or error
            this.okButton.setAttribute("disabled", "true");
            this.sdkInstance.uploadFile(this.files[0], this.uploadHandlerCallback.bind(this), this.$app.onEngageResponse
            .bind(this.$app));
        } else {
            const msg = appStringProps.emptyFileError || "Please select a file";
            this.setError(msg);
        }
    },

    /**
     * FileUpload handler called when file is uploaded to server or failed
     * @param {{status :string, type :string}} response Upload File response from
     */
    uploadHandlerCallback({ status, type }) {
        const appStringProps = this.$app.getUIConfig().appStringProps;
        const msg = appStringProps[type] || "File Upload Failed";
        this.okButton.removeAttribute("disabled");

        if (status === "error") {
            this.setError(msg);
            this.sdkInstance.sendSystemMessage(msg);

            const file = this.files[0] as File;
            let fileDetails;
            if (file) {
                fileDetails = {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                };
            }

            this.$app.sdkInst.logError(`File Upload Failed: ${JSON.stringify({status, type})} ${JSON.stringify(fileDetails)}`);
        } else if (status === "success") {
            addMessageBubbleToTranscriptInChatWindow(
                this.$app,
                appStringProps.uploadSuccessText,
                SYSTEM_STATE.FILE_UPLOAD_SUCCESS
            );
            this.onCancelClick();
        }
    },

    removed() {
        this.unBindKeyboardShortcuts();
        this.$app.getStateMgr().setState({ isModalVisible: false });

        if (this.richInstance) {
            this.richInstance.clearAll();
        }
    },

    /**
     * Resets Error if File is uploaded
     */
    resetError() {
        const msgElement = this.el.querySelector(".error-msg");

        if (msgElement) {
            msgElement.innerHTML = "";
        }
    },

    /**
     * Sets Error Message and Read with Aria Reader
     * @param {string} msg Any arbitrary error string
     */
    setError(msg) {
        const msgElement = this.el.querySelector(".error-msg");

        if (msgElement) {
            msgElement.innerHTML = msg;
            this.$app.getStateMgr().setState({ ariaReaderSay: msg });
        }
    },

    /**
     * Unbind keyboard shortcuts
     */
    unBindKeyboardShortcuts() {
        this.mouseTrap.unbind("escape");
        this.mouseTrap.unbind("enter");
        this.mouseTrap.unbind("tab");
        this.mouseTrap.unbind("shift+tab");
    },
};

export default appInstance => {
    FileUploadModalView.$app = appInstance;
    const constObj: any = {};
    FileUploadModalView.constants = constObj;
    return FileUploadModalView;
};
